import { useState, useEffect, useCallback, useRef } from "react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { dbGet, dbPatch, dbPost, dbDelete } from "./supabase";
import { SignOutButton, useAuth } from "./auth";

const C = {
  bg: "#F0F4F8", surface: "#FFFFFF", card: "#FFFFFF",
  sidebar: "#1A2740", sidebarActive: "#2E4270",
  border: "#E2E8F2", blue: "#2563EB", blueLight: "#EFF6FF",
  cyan: "#0891B2", green: "#059669", greenLight: "#ECFDF5",
  amber: "#D97706", amberLight: "#FFFBEB",
  rose: "#E11D48", roseLight: "#FFF1F2",
  violet: "#7C3AED", violetLight: "#F5F3FF",
  text: "#0F172A", textSoft: "#64748B", textMid: "#334155",
  textSidebar: "#94A3B8",
};
const FONT = "'DM Sans','Segoe UI',sans-serif";
const MONO = "'DM Mono','Fira Mono',monospace";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => {
  const abs = Math.abs(Number(n));
  const str = abs >= 1000 ? abs.toLocaleString() : abs.toString();
  return n < 0 ? `-$${str}` : `$${str}`;
};
const fmtK = (n) => n >= 1000 ? `$${(n/1000).toFixed(0)}k` : `$${n}`;

// ── Claude API helper ──────────────────────────────────────────────────────────
async function callClaude(messages, system) {
  const resp = await fetch("/api/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, system }),
  });
  const data = await resp.json();
  if (!resp.ok || data.error) throw new Error(data.error?.message || `API error ${resp.status}`);
  return data.content?.[0]?.text ?? "";
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Spinner() {
  return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200, color:C.textSoft, fontFamily:FONT, fontSize:13 }}>Loading from database…</div>;
}

function StatCard({ label, value, sub, icon }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 24px", display:"flex", flexDirection:"column", gap:6, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <span style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", fontFamily:FONT }}>{label}</span>
        <span style={{ fontSize:20 }}>{icon}</span>
      </div>
      <div style={{ fontSize:28, fontWeight:700, color:C.text, fontFamily:MONO, letterSpacing:-1 }}>{value}</div>
      {sub && <div style={{ fontSize:13, color:C.textSoft, fontFamily:FONT }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ current, target, color }) {
  const pct = Math.min((current/target)*100, 100);
  return (
    <div style={{ background:C.border, borderRadius:99, height:8, overflow:"hidden" }}>
      <div style={{ width:`${pct}%`, height:"100%", background:color, borderRadius:99 }} />
    </div>
  );
}

function Avatar({ initials, color, size=36 }) {
  return (
    <div style={{ width:size, height:size, borderRadius:size/2, background:color+"22", border:`2px solid ${color}44`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
      <span style={{ fontSize:size*0.35, fontWeight:700, color, fontFamily:FONT }}>{initials}</span>
    </div>
  );
}

function EditableNum({ value, onSave, prefix="$", style={} }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  const commit = () => { setEditing(false); const n = parseFloat(val); if (!isNaN(n)) onSave(n); else setVal(value); };
  if (editing) return (
    <input autoFocus value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape"){setEditing(false);setVal(value);}}}
      style={{ width:90, fontSize:13, fontFamily:MONO, color:C.blue, border:`1px solid ${C.blue}`, borderRadius:6, padding:"2px 6px", outline:"none", background:C.blueLight, textAlign:"right", ...style }} />
  );
  return <span onClick={()=>setEditing(true)} style={{ cursor:"pointer", borderBottom:`1px dashed ${C.border}`, fontFamily:MONO, ...style }}>{prefix}{Number(value).toLocaleString()}</span>;
}

// ── AI INSIGHTS CARD ──────────────────────────────────────────────────────────
function AIInsightsCard() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const generate = async () => {
    setLoading(true); setError(null);
    try {
      const [assets, liabilities, goals, members, profiles, accounts, transactions] = await Promise.all([
        dbGet("assets", "?order=value.desc"),
        dbGet("liabilities"),
        dbGet("goals"),
        dbGet("family_members", "?order=created_at.asc"),
        dbGet("member_profiles"),
        dbGet("member_accounts", "?order=sort_order.asc"),
        dbGet("transactions", "?order=transaction_date.desc&limit=30"),
      ]);
      const totalAssets  = assets.reduce((s,a) => s + Number(a.value), 0);
      const totalLiab    = liabilities.reduce((s,l) => s + Number(l.balance), 0);
      const netWorth     = totalAssets - totalLiab;
      const totalIncome  = profiles.reduce((s,p) => s + (p.salary || 0), 0);
      const debts        = accounts.filter(a => a.is_debt);
      const totalDebt    = debts.reduce((s,d) => s + (d.balance || 0), 0);
      const monthlySpend = transactions.filter(t => Number(t.amount) < 0)
                                       .reduce((s,t) => s + Math.abs(Number(t.amount)), 0);
      const context =
`Harper Family — March 2026
Members: ${members.map(m=>m.name).join(", ")}
Net worth: $${netWorth.toLocaleString()} | Assets: $${totalAssets.toLocaleString()} | Debt: $${totalLiab.toLocaleString()}
Combined income: $${totalIncome.toLocaleString()}/yr ($${Math.round(totalIncome/12).toLocaleString()}/mo)
Recent monthly spend: $${Math.round(monthlySpend).toLocaleString()}
Personal loans: $${totalDebt.toLocaleString()} across ${debts.length} accounts
Debt detail: ${debts.map(d=>`${d.label} $${(d.balance||0).toLocaleString()} @ ${d.interest_rate||'?'}%`).join(", ")}
Goals: ${goals.map(g=>`${g.name} ${Math.round((g.current_amount/g.target_amount)*100)}% funded`).join(", ")}`;

      const text = await callClaude(
        [{ role:"user", content:"Give me 3 concise financial insights for this family. Use real numbers." }],
        `You are a financial advisor reviewing the Harper family's finances. Give exactly 3 short, specific insights — each one sentence, starting with an emoji. Use real numbers from the data. Be direct.\n\n${context}`
      );
      const lines = text.split("\n").map(l=>l.trim()).filter(l=>l.length > 10);
      setInsights(lines.slice(0,3));
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: (insights||loading||error) ? 16 : 0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:16 }}>✨</span>
          <span style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", fontFamily:FONT }}>AI Financial Insights</span>
        </div>
        <button onClick={generate} disabled={loading}
          style={{ background:loading?C.border:C.violet, color:loading?C.textSoft:"#fff", border:"none", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:600, fontFamily:FONT, cursor:loading?"default":"pointer" }}>
          {loading ? "Generating…" : insights ? "↺ Refresh" : "Generate Insights"}
        </button>
      </div>
      {!insights && !loading && !error && (
        <div style={{ fontSize:13, color:C.textSoft, fontFamily:FONT, textAlign:"center", paddingTop:8 }}>
          Get personalised insights powered by Claude — based on your live data.
        </div>
      )}
      {error && (
        <div style={{ padding:"10px 14px", background:C.roseLight, borderRadius:10, fontSize:12, color:C.rose, fontFamily:FONT }}>
          {error.includes("CLAUDE_API_KEY") || error.includes("503") || error.includes("404")
            ? "⚙️ Claude API not configured yet — add CLAUDE_API_KEY to Vercel to activate."
            : `Error: ${error}`}
        </div>
      )}
      {loading && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[1,2,3].map(i=>(
            <div key={i} style={{ height:40, background:C.bg, borderRadius:10, opacity:0.6 }} />
          ))}
        </div>
      )}
      {insights && !loading && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {insights.map((ins,i)=>(
            <div key={i} style={{ padding:"12px 16px", background:[C.blueLight,C.greenLight,C.violetLight][i]||C.bg, borderRadius:12, fontSize:13, color:C.text, fontFamily:FONT, lineHeight:1.55 }}>
              {ins}
            </div>
          ))}
          <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT, textAlign:"right" }}>Powered by Claude · {new Date().toLocaleDateString()}</div>
        </div>
      )}
    </div>
  );
}

// ── OVERVIEW ──────────────────────────────────────────────────────────────────
function OverviewSection() {
  const [assets, setAssets] = useState(null);
  const [liabilities, setLiabilities] = useState(null);
  const [goals, setGoals] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [members, setMembers] = useState(null);

  useEffect(() => {
    Promise.all([
      dbGet("assets", "?order=value.desc"),
      dbGet("liabilities"),
      dbGet("goals"),
      dbGet("transactions", "?order=transaction_date.desc&limit=6"),
      dbGet("family_members", "?order=created_at.asc"),
    ]).then(([a,l,g,t,m]) => { setAssets(a); setLiabilities(l); setGoals(g); setTransactions(t); setMembers(m); });
  }, []);

  if (!assets) return <Spinner />;

  const totalAssets = assets.reduce((s,a)=>s+Number(a.value),0);
  const totalLiab = liabilities.reduce((s,l)=>s+Number(l.balance),0);
  const netWorth = totalAssets - totalLiab;

  const nwChartData = [
    {m:"Aug",v:142000},{m:"Sep",v:148000},{m:"Oct",v:151000},
    {m:"Nov",v:155000},{m:"Dec",v:159000},{m:"Jan",v:163000},
    {m:"Feb",v:168000},{m:"Mar",v:netWorth},
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
        <StatCard label="Net Worth" value={fmt(netWorth)} sub="Live from database" icon="💎" />
        <StatCard label="Total Assets" value={fmt(totalAssets)} sub={`${assets.length} assets tracked`} icon="📈" />
        <StatCard label="Total Debt" value={fmt(totalLiab)} sub={`${liabilities.length} liabilities`} icon="📉" />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr", gap:16 }}>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Net Worth Trend</div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={nwChartData}>
              <defs>
                <linearGradient id="nwg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.blue} stopOpacity={0.12}/>
                  <stop offset="95%" stopColor={C.blue} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="m" tick={{fontSize:11,fill:C.textSoft,fontFamily:FONT}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:C.textSoft,fontFamily:MONO}} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
              <Tooltip formatter={v=>[fmt(v),"Net Worth"]} contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,fontSize:12,fontFamily:FONT}}/>
              <Area type="monotone" dataKey="v" stroke={C.blue} strokeWidth={2.5} fill="url(#nwg)" dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Recent Transactions</div>
          {transactions.length === 0 ? (
            <div style={{ fontSize:13, color:C.textSoft, fontFamily:FONT, textAlign:"center", padding:"20px 0" }}>No transactions yet.<br/>Add them in Monthly Spend.</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {transactions.map(t => {
                const member = members?.find(m=>m.initials===t.who);
                return (
                  <div key={t.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      {member && <Avatar initials={member.initials} color={member.color} size={26}/>}
                      <div>
                        <div style={{ fontSize:12, fontWeight:500, color:C.text, fontFamily:FONT }}>{t.description}</div>
                        <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT }}>{t.transaction_date}</div>
                      </div>
                    </div>
                    <span style={{ fontSize:12, fontWeight:600, color:Number(t.amount)>0?C.green:C.text, fontFamily:MONO }}>{fmt(t.amount)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:20, fontFamily:FONT }}>Family Goals — Live from Database</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:20 }}>
          {goals.map(g => (
            <div key={g.id}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{g.icon} {g.name}</span>
                <span style={{ fontSize:12, fontFamily:MONO, color:C.textSoft }}>{fmt(g.current_amount)} / {fmt(g.target_amount)}</span>
              </div>
              <ProgressBar current={Number(g.current_amount)} target={Number(g.target_amount)} color={g.color}/>
              <div style={{ fontSize:11, color:C.textSoft, marginTop:4, fontFamily:FONT }}>{Math.round((g.current_amount/g.target_amount)*100)}% funded</div>
            </div>
          ))}
        </div>
      </div>

      <AIInsightsCard />
    </div>
  );
}

// ── MONTHLY SPEND / BUDGET ────────────────────────────────────────────────────
function MoneySection() {
  const [categories, setCategories] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [members, setMembers] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTx, setNewTx] = useState({ description:"", category:"", amount:"", transaction_date: new Date().toISOString().split("T")[0], who:"MH" });

  const load = useCallback(() => {
    Promise.all([
      dbGet("budget_categories", "?order=budget_amount.desc"),
      dbGet("transactions", "?order=transaction_date.desc"),
      dbGet("family_members", "?order=created_at.asc"),
    ]).then(([c,t,m]) => { setCategories(c); setTransactions(t); setMembers(m); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateBudget = async (id, val) => {
    await dbPatch("budget_categories", id, { budget_amount: val });
    setCategories(prev => prev.map(c => c.id===id ? {...c, budget_amount:val} : c));
  };

  const addTransaction = async () => {
    if (!newTx.description || !newTx.amount) return;
    await dbPost("transactions", { ...newTx, amount: Number(newTx.amount) });
    setShowAdd(false);
    setNewTx({ description:"", category:"", amount:"", transaction_date: new Date().toISOString().split("T")[0], who:"MH" });
    load();
  };

  const deleteTransaction = async (id) => {
    await dbDelete("transactions", id);
    setTransactions(prev => prev.filter(t=>t.id!==id));
  };

  if (!categories) return <Spinner />;

  const spendByCategory = categories.map(cat => ({
    ...cat,
    spent: transactions?.filter(t=>t.category===cat.name && Number(t.amount)<0).reduce((s,t)=>s+Math.abs(Number(t.amount)),0) || 0,
  }));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Spending by Category</div>
          <div style={{ display:"flex", justifyContent:"center" }}>
            <PieChart width={180} height={180}>
              <Pie data={spendByCategory.filter(c=>c.spent>0)} dataKey="spent" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                {spendByCategory.map((d,i)=><Cell key={i} fill={d.color}/>)}
              </Pie>
              <Tooltip formatter={v=>[`$${v}`,""]} contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,fontSize:12,fontFamily:FONT}}/>
            </PieChart>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5, marginTop:8 }}>
            {spendByCategory.map(d=>(
              <div key={d.name} style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:8, height:8, borderRadius:2, background:d.color, flexShrink:0 }}/>
                <span style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{d.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Top Spend</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={spendByCategory.filter(c=>c.spent>0).slice(0,6)} layout="vertical" margin={{left:0,right:16}}>
              <XAxis type="number" tick={{fontSize:11,fill:C.textSoft,fontFamily:MONO}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`}/>
              <YAxis type="category" dataKey="name" tick={{fontSize:11,fill:C.textMid,fontFamily:FONT}} axisLine={false} tickLine={false} width={80}/>
              <Tooltip formatter={v=>[`$${v}`,""]} contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,fontSize:12,fontFamily:FONT}}/>
              <Bar dataKey="spent" fill={C.blue} radius={[0,6,6,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Budget tracker */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:20, fontFamily:FONT }}>Budget Tracker — click budget to edit · saves to database</div>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {spendByCategory.map(d => {
            const pct = d.budget_amount > 0 ? Math.min((d.spent/d.budget_amount)*100,100) : 0;
            const over = d.spent > d.budget_amount && d.budget_amount > 0;
            const barColor = pct>90?C.rose:pct>75?C.amber:C.green;
            return (
              <div key={d.id}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                  <span style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{d.name}</span>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    {over && <span style={{ fontSize:10, fontWeight:700, color:C.rose, background:C.roseLight, padding:"2px 8px", borderRadius:99, fontFamily:FONT }}>OVER</span>}
                    <span style={{ fontSize:13, fontFamily:MONO, color:over?C.rose:C.textSoft }}>${d.spent.toLocaleString()}</span>
                    <span style={{ color:C.textSoft }}>/</span>
                    <EditableNum value={d.budget_amount} onSave={v=>updateBudget(d.id,v)} style={{ fontSize:13, color:C.textSoft }}/>
                  </div>
                </div>
                <div style={{ background:C.border, borderRadius:99, height:7, overflow:"hidden" }}>
                  <div style={{ width:`${pct}%`, height:"100%", background:barColor, borderRadius:99 }}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transactions */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", fontFamily:FONT }}>Transactions</div>
          <button onClick={()=>setShowAdd(true)} style={{ background:C.blue, color:"#fff", border:"none", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:600, fontFamily:FONT, cursor:"pointer" }}>+ Add</button>
        </div>
        {transactions?.length === 0 ? (
          <div style={{ textAlign:"center", padding:"20px 0", color:C.textSoft, fontFamily:FONT, fontSize:13 }}>No transactions yet. Add your first one!</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {transactions?.map(t=>(
              <div key={t.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:C.bg, borderRadius:10 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{t.description}</div>
                  <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{t.transaction_date} · {t.category} · {t.who}</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:Number(t.amount)>0?C.green:C.rose, fontFamily:MONO }}>{fmt(t.amount)}</span>
                  <span onClick={()=>deleteTransaction(t.id)} style={{ fontSize:11, color:C.textSoft, cursor:"pointer", padding:"2px 6px", borderRadius:4, background:C.border }}>✕</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add transaction modal */}
      {showAdd && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }}>
          <div style={{ background:C.surface, borderRadius:18, padding:28, width:420, boxShadow:"0 20px 60px rgba(0,0,0,0.15)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:C.text, fontFamily:FONT, marginBottom:20 }}>Add Transaction</div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {[
                ["Description", "description", "text", "e.g. Whole Foods"],
                ["Amount (negative = expense)", "amount", "number", "e.g. -124.50"],
                ["Date", "transaction_date", "date", ""],
              ].map(([label, field, type, ph])=>(
                <div key={field}>
                  <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT, marginBottom:4 }}>{label}</div>
                  <input type={type} placeholder={ph} value={newTx[field]}
                    onChange={e=>setNewTx(p=>({...p,[field]:e.target.value}))}
                    style={{ width:"100%", padding:"9px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontFamily:FONT, outline:"none", color:C.text, boxSizing:"border-box" }}/>
                </div>
              ))}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT, marginBottom:4 }}>Category</div>
                  <select value={newTx.category} onChange={e=>setNewTx(p=>({...p,category:e.target.value}))}
                    style={{ width:"100%", padding:"9px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontFamily:FONT, outline:"none", color:C.text, background:C.surface }}>
                    <option value="">Select…</option>
                    {categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                    <option value="Income">Income</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT, marginBottom:4 }}>Who</div>
                  <select value={newTx.who} onChange={e=>setNewTx(p=>({...p,who:e.target.value}))}
                    style={{ width:"100%", padding:"9px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontFamily:FONT, outline:"none", color:C.text, background:C.surface }}>
                    {members?.map(m=><option key={m.id} value={m.initials}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:4 }}>
                <button onClick={()=>setShowAdd(false)} style={{ padding:"9px 20px", border:`1px solid ${C.border}`, borderRadius:10, fontSize:13, fontFamily:FONT, cursor:"pointer", background:C.bg, color:C.textSoft }}>Cancel</button>
                <button onClick={addTransaction} style={{ padding:"9px 20px", border:"none", borderRadius:10, fontSize:13, fontFamily:FONT, cursor:"pointer", background:C.blue, color:"#fff", fontWeight:600 }}>Save to Database</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── FAMILY MEMBERS ────────────────────────────────────────────────────────────
function FamilySection() {
  const [members, setMembers]   = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([
      dbGet("family_members", "?order=created_at.asc"),
      dbGet("member_profiles", "?order=id.asc"),
      dbGet("member_accounts",  "?order=sort_order.asc"),
    ]).then(([m, p, a]) => {
      setMembers(m);
      setProfiles(p || []);
      setAccounts(a || []);
    });
  }, []);

  if (!members) return <Spinner />;

  const age = (dob) => {
    if (!dob) return "";
    const b = new Date(dob), today = new Date();
    let a = today.getFullYear() - b.getFullYear();
    if (today.getMonth() < b.getMonth() || (today.getMonth() === b.getMonth() && today.getDate() < b.getDate())) a--;
    return a;
  };

  const nextBirthdayDays = (dob) => {
    if (!dob) return null;
    const b = new Date(dob), today = new Date();
    let next = new Date(today.getFullYear(), b.getMonth(), b.getDate());
    if (next < today) next = new Date(today.getFullYear() + 1, b.getMonth(), b.getDate());
    const diff = Math.ceil((next - today) / (1000 * 60 * 60 * 24));
    return diff <= 30 ? diff : null;
  };

  const creditColor = (score) => {
    if (!score) return C.textSoft;
    if (score >= 800) return C.green;
    if (score >= 740) return C.blue;
    if (score >= 670) return C.amber;
    return C.rose;
  };

  const fmt$ = (n) => n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

  const enrich = (m) => ({
    ...m,
    profile:  profiles.find(p => p.member_id === m.id) || null,
    accounts: accounts.filter(a => a.member_id === m.id),
  });

  const parents = members.filter(m => m.role === "parent").map(enrich);
  const kids    = members.filter(m => m.role === "child").map(enrich);

  const totalIncome     = parents.reduce((s, m) => s + (m.profile?.salary || 0), 0);
  const totalRetirement = [...parents, ...kids].reduce((s, m) =>
    s + m.accounts.filter(a => a.account_type === "retirement" && !a.is_debt).reduce((x, a) => x + (a.value || 0), 0), 0);
  const total529        = kids.reduce((s, m) =>
    s + m.accounts.filter(a => a.account_type === "529").reduce((x, a) => x + (a.value || 0), 0), 0);
  const totalDebt       = [...parents, ...kids].reduce((s, m) =>
    s + m.accounts.filter(a => a.is_debt).reduce((x, a) => x + (a.balance || 0), 0), 0);

  const summaryStats = [
    { label: "Household Income",  value: fmt$(totalIncome),     sub: "/year",              color: C.green,  icon: "💼" },
    { label: "Total Retirement",  value: fmt$(totalRetirement), sub: "all accounts",        color: C.blue,   icon: "📈" },
    { label: "529 College Funds", value: fmt$(total529),        sub: `${kids.length} kids`, color: C.violet, icon: "🎓" },
    { label: "Personal Debt",     value: fmt$(totalDebt),       sub: "loans",               color: C.rose,   icon: "📋" },
  ];

  const MemberCard = ({ m, isParent }) => {
    const profile      = m.profile;
    const assetAccts   = m.accounts.filter(a => !a.is_debt);
    const debtAccts    = m.accounts.filter(a => a.is_debt);
    const totalAssets  = assetAccts.reduce((s, a) => s + (a.value || 0), 0);
    const totalOwed    = debtAccts.reduce((s, a) => s + (a.balance || 0), 0);
    const bdays        = nextBirthdayDays(m.birthday);

    return (
      <div
        onClick={() => setSelected(m)}
        style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)", cursor: "pointer", position: "relative", overflow: "hidden",
          transition: "box-shadow 0.2s, transform 0.2s, border-color 0.2s" }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = m.color; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = C.border; }}
      >
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: m.color, borderRadius: "16px 16px 0 0" }} />
        {bdays && (
          <div style={{ position: "absolute", top: 12, right: 12, background: C.amberLight, borderRadius: 20,
            padding: "3px 8px", fontSize: 10, fontWeight: 700, color: C.amber, fontFamily: FONT }}>
            🎂 {bdays}d
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, marginTop: 8 }}>
          <Avatar initials={m.initials} color={m.color} size={isParent ? 48 : 44} />
          <div>
            <div style={{ fontSize: isParent ? 17 : 15, fontWeight: 700, color: C.text, fontFamily: FONT }}>{m.name}</div>
            <div style={{ fontSize: 12, color: C.textSoft, fontFamily: FONT, marginTop: 1 }}>
              Age {age(m.birthday)} · {isParent ? "Parent" : "Child"}
            </div>
            {(isParent ? profile?.employer : profile?.title) && (
              <div style={{ fontSize: 11, color: C.textSoft, fontFamily: FONT, marginTop: 1 }}>
                {isParent ? profile.employer : profile.title}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          {isParent && profile?.salary && (
            <div style={{ background: C.greenLight, borderRadius: 10, padding: "9px 12px" }}>
              <div style={{ fontSize: 9, color: C.green, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: FONT }}>Income</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.green, fontFamily: MONO, marginTop: 2 }}>{fmt$(profile.salary)}</div>
              <div style={{ fontSize: 9, color: C.green, opacity: 0.6, fontFamily: FONT }}>/year</div>
            </div>
          )}
          {!isParent && profile?.part_time_wage && (
            <div style={{ background: C.greenLight, borderRadius: 10, padding: "9px 12px" }}>
              <div style={{ fontSize: 9, color: C.green, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: FONT }}>Wage</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.green, fontFamily: MONO, marginTop: 2 }}>${profile.part_time_wage}/hr</div>
              <div style={{ fontSize: 9, color: C.green, opacity: 0.6, fontFamily: FONT }}>~{profile.part_time_hours} hrs/wk</div>
            </div>
          )}
          {totalAssets > 0 && (
            <div style={{ background: C.blueLight, borderRadius: 10, padding: "9px 12px" }}>
              <div style={{ fontSize: 9, color: C.blue, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: FONT }}>Assets</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.blue, fontFamily: MONO, marginTop: 2 }}>{fmt$(totalAssets)}</div>
              <div style={{ fontSize: 9, color: C.blue, opacity: 0.6, fontFamily: FONT }}>{assetAccts.length} account{assetAccts.length !== 1 ? "s" : ""}</div>
            </div>
          )}
          {totalOwed > 0 && (
            <div style={{ background: C.roseLight, borderRadius: 10, padding: "9px 12px" }}>
              <div style={{ fontSize: 9, color: C.rose, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: FONT }}>Loans</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.rose, fontFamily: MONO, marginTop: 2 }}>{fmt$(totalOwed)}</div>
              <div style={{ fontSize: 9, color: C.rose, opacity: 0.6, fontFamily: FONT }}>{debtAccts.length} loan{debtAccts.length !== 1 ? "s" : ""}</div>
            </div>
          )}
          {profile?.credit_score && (
            <div style={{ background: C.greenLight, borderRadius: 10, padding: "9px 12px" }}>
              <div style={{ fontSize: 9, color: C.green, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: FONT }}>Credit</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: creditColor(profile.credit_score), fontFamily: MONO, marginTop: 2 }}>{profile.credit_score}</div>
              <div style={{ fontSize: 9, color: creditColor(profile.credit_score), opacity: 0.8, fontFamily: FONT }}>{profile.credit_label}</div>
            </div>
          )}
          {!isParent && !profile?.part_time_wage && totalAssets === 0 && totalOwed === 0 && (
            <div style={{ gridColumn: "1/-1", background: m.color + "11", border: `1px solid ${m.color}33`, borderRadius: 10, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: C.textSoft, fontFamily: FONT, marginBottom: 2 }}>Next birthday</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: m.color, fontFamily: FONT }}>
                {m.birthday ? new Date(m.birthday).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : "—"}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 11, color: C.textSoft, fontFamily: FONT }}>
            {m.birthday ? new Date(m.birthday).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: m.color, fontFamily: FONT }}>View profile →</span>
        </div>
      </div>
    );
  };

  const MemberModal = ({ m }) => {
    const profile     = m.profile;
    const assetAccts  = m.accounts.filter(a => !a.is_debt);
    const debtAccts   = m.accounts.filter(a => a.is_debt);
    const totalAssets = assetAccts.reduce((s, a) => s + (a.value || 0), 0);
    const totalOwed   = debtAccts.reduce((s, a) => s + (a.balance || 0), 0);
    const bdays       = nextBirthdayDays(m.birthday);

    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(15,23,42,0.5)",
        backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
        onClick={() => setSelected(null)}>
        <div style={{ background: C.card, borderRadius: 20, width: "100%", maxWidth: 540,
          maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background: "linear-gradient(135deg, #1A2740 0%, #2B3F63 100%)",
            borderRadius: "20px 20px 0 0", padding: "28px 28px 24px", position: "relative" }}>
            <button onClick={() => setSelected(null)} style={{ position: "absolute", top: 16, right: 16,
              background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, width: 32, height: 32,
              cursor: "pointer", color: "#fff", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
              ×
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <Avatar initials={m.initials} color={m.color} size={56} />
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", fontFamily: FONT }}>{m.name}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", fontFamily: FONT, marginTop: 2 }}>
                  {profile?.title}{profile?.employer ? ` · ${profile.employer}` : ""}
                </div>
                {bdays && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6,
                    padding: "3px 10px", background: C.amber, borderRadius: 20, fontSize: 11, fontWeight: 600, color: "#fff", fontFamily: FONT }}>
                    🎂 Birthday in {bdays} day{bdays !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${[true, profile?.salary || profile?.part_time_wage, profile?.credit_score].filter(Boolean).length}, 1fr)`, gap: 10 }}>
              <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: FONT, marginBottom: 4 }}>Age</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: MONO }}>{age(m.birthday)}</div>
              </div>
              {profile?.salary && (
                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: FONT, marginBottom: 4 }}>Annual Salary</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: MONO }}>{fmt$(profile.salary)}</div>
                </div>
              )}
              {profile?.part_time_wage && (
                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: FONT, marginBottom: 4 }}>Wage</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: MONO }}>${profile.part_time_wage}/hr</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", fontFamily: FONT }}>~{profile.part_time_hours} hrs/wk</div>
                </div>
              )}
              {profile?.credit_score && (
                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: FONT, marginBottom: 4 }}>Credit Score</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: creditColor(profile.credit_score), fontFamily: MONO }}>{profile.credit_score}</div>
                  <div style={{ fontSize: 10, color: creditColor(profile.credit_score), fontFamily: FONT }}>{profile.credit_label}</div>
                </div>
              )}
            </div>
          </div>
          <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 20 }}>
            {assetAccts.length > 0 && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textSoft, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: FONT }}>Accounts & Savings</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.blue, fontFamily: MONO }}>{fmt$(totalAssets)}</div>
                </div>
                <div style={{ background: C.bg, borderRadius: 12, padding: "4px 14px" }}>
                  {assetAccts.map(a => {
                    const typeColor = { retirement: C.blue, hsa: C.green, "529": C.violet }[a.account_type] || C.blue;
                    return (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 14 }}>{a.icon}</span>
                          <span style={{ fontSize: 12, color: C.textMid, fontWeight: 500, fontFamily: FONT }}>{a.label}</span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: typeColor, fontFamily: MONO }}>{fmt$(a.value)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {debtAccts.length > 0 && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textSoft, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: FONT }}>Loans</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.rose, fontFamily: MONO }}>{fmt$(totalOwed)}</div>
                </div>
                <div style={{ background: C.roseLight, borderRadius: 12, padding: "4px 14px" }}>
                  {debtAccts.map(a => {
                    const monthlyInterest = a.interest_rate ? (a.balance * (a.interest_rate / 100)) / 12 : 0;
                    return (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "8px 0", borderBottom: `1px solid ${C.rose}22` }}>
                        <div>
                          <div style={{ fontSize: 12, color: C.textMid, fontWeight: 500, fontFamily: FONT }}>{a.label}</div>
                          {a.interest_rate && (
                            <div style={{ fontSize: 10, color: C.textSoft, fontFamily: FONT }}>
                              {a.interest_rate}% APR · {fmt$(monthlyInterest)}/mo interest
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.rose, fontFamily: MONO }}>{fmt$(a.balance)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ background: C.bg, borderRadius: 14, padding: "16px 18px",
              display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: 11, color: C.textSoft, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: FONT }}>Personal Net Position</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: C.text, fontFamily: MONO, marginTop: 4 }}>{fmt$(totalAssets - totalOwed)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: C.green, fontWeight: 600, fontFamily: FONT }}>↑ {fmt$(totalAssets)} assets</div>
                <div style={{ fontSize: 11, color: C.rose, fontWeight: 600, fontFamily: FONT, marginTop: 2 }}>↓ {fmt$(totalOwed)} debt</div>
              </div>
            </div>
            {profile?.note && (
              <div style={{ padding: "10px 14px", background: C.amberLight, borderRadius: 10,
                borderLeft: `3px solid ${C.amber}`, fontSize: 12, color: "#92400E", fontStyle: "italic", fontFamily: FONT }}>
                {profile.note}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
        {summaryStats.map((s, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
            padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16 }}>{s.icon}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.textSoft, letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: FONT }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: MONO }}>{s.value}</div>
            <div style={{ fontSize: 10, color: C.textSoft, marginTop: 2, fontFamily: FONT }}>{s.sub}</div>
          </div>
        ))}
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.textSoft, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12, fontFamily: FONT }}>Parents</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {parents.map(m => <MemberCard key={m.id} m={m} isParent={true} />)}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.textSoft, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12, fontFamily: FONT }}>Kids</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
          {kids.map(m => <MemberCard key={m.id} m={m} isParent={false} />)}
        </div>
      </div>
      {selected && <MemberModal m={selected} />}
    </div>
  );
}

// ── ASSETS & PROPERTY ─────────────────────────────────────────────────────────
function AssetsSection() {
  const [assets, setAssets] = useState(null);
  const [liabilities, setLiabilities] = useState(null);
  const [home, setHome] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      dbGet("assets","?order=value.desc"),
      dbGet("liabilities","?order=balance.desc"),
      dbGet("home_config","?limit=1"),
    ]).then(([a,l,h])=>{ setAssets(a); setLiabilities(l); setHome(h[0]); });
  },[]);

  useEffect(()=>{ load(); },[load]);

  const updateAsset = async (id,val) => {
    await dbPatch("assets",id,{value:val});
    setAssets(prev=>prev.map(a=>a.id===id?{...a,value:val}:a));
  };
  const updateLiability = async (id,field,val) => {
    await dbPatch("liabilities",id,{[field]:val});
    setLiabilities(prev=>prev.map(l=>l.id===id?{...l,[field]:val}:l));
  };
  const updateHome = async (field,val) => {
    await dbPatch("home_config",home.id,{[field]:val});
    setHome(prev=>({...prev,[field]:val}));
  };

  if (!assets || !home) return <Spinner />;

  const totalAssets = assets.reduce((s,a)=>s+Number(a.value),0);
  const totalLiab = liabilities.reduce((s,l)=>s+Number(l.balance),0);
  const netWorth = totalAssets - totalLiab;
  const netEquity = Number(home.estimated_value) - Number(home.mortgage_balance) - Number(home.heloc_used);
  const helocAvail = Number(home.heloc_limit) - Number(home.heloc_used);
  const ltv = ((Number(home.mortgage_balance)+Number(home.heloc_used))/Number(home.estimated_value)*100).toFixed(1);
  const studentLoans = liabilities.filter(l=>l.icon==="🎓");
  const totalStudentDebt = studentLoans.reduce((s,l)=>s+Number(l.balance),0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
        <StatCard label="Total Assets" value={fmt(totalAssets)} icon="📈"/>
        <StatCard label="Total Liabilities" value={fmt(totalLiab)} icon="📉"/>
        <StatCard label="Net Worth" value={fmt(netWorth)} icon="💎"/>
      </div>

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>🏡 Home Equity · {home.address}</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:20 }}>
          {[
            ["Est. Value", home.estimated_value, C.green, "estimated_value", ""],
            ["Mortgage", home.mortgage_balance, C.rose, "mortgage_balance", home.mortgage_lender],
            ["HELOC Used", home.heloc_used, C.amber, "heloc_used", `of ${fmt(home.heloc_limit)} limit`],
            ["Net Equity", netEquity, C.blue, null, "Your ownership"],
            ["HELOC Available", helocAvail, C.green, null, `LTV: ${ltv}%`],
          ].map(([label,value,color,field,sub])=>(
            <div key={label} style={{ background:C.bg, borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT, marginBottom:4, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
              {field ? (
                <EditableNum value={value} onSave={v=>updateHome(field,v)} style={{ fontSize:16, fontWeight:700, color }}/>
              ) : (
                <div style={{ fontSize:16, fontWeight:700, color, fontFamily:MONO }}>{fmt(value)}</div>
              )}
              <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT, marginTop:2 }}>{sub}</div>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", borderRadius:99, overflow:"hidden", height:12, marginBottom:8 }}>
          <div style={{ width:`${(Number(home.mortgage_balance)/Number(home.estimated_value))*100}%`, background:C.rose }}/>
          <div style={{ width:`${(Number(home.heloc_used)/Number(home.estimated_value))*100}%`, background:C.amber }}/>
          <div style={{ flex:1, background:C.green+"44" }}/>
        </div>
        <div style={{ display:"flex", gap:16 }}>
          {[["Mortgage",C.rose],["HELOC Used",C.amber],["Equity",C.green+"88"]].map(([l,c])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:5 }}>
              <div style={{ width:8, height:8, borderRadius:2, background:c }}/>
              <span style={{ fontSize:10, color:C.textSoft, fontFamily:FONT }}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", fontFamily:FONT }}>🎓 Student Loan Tracker — click to edit</div>
          <div style={{ fontSize:13, fontFamily:MONO, color:C.rose, fontWeight:700 }}>Total: {fmt(totalStudentDebt)}</div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {studentLoans.map(l=>{
            const monthlyInt = l.interest_rate && l.balance ? Math.round(Number(l.balance)*(Number(l.interest_rate)/100)/12) : null;
            return (
              <div key={l.id} style={{ display:"grid", gridTemplateColumns:"1fr 130px 130px 130px", gap:12, alignItems:"center", padding:"12px 16px", background:C.bg, borderRadius:10 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{l.name}</div>
                  <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{l.owner}</div>
                </div>
                <div>
                  <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT, marginBottom:2 }}>Balance</div>
                  <EditableNum value={l.balance} onSave={v=>updateLiability(l.id,"balance",v)} style={{ fontSize:13, fontWeight:700, color:Number(l.balance)>0?C.rose:C.textSoft }}/>
                </div>
                <div>
                  <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT, marginBottom:2 }}>Rate %</div>
                  <EditableNum value={l.interest_rate||0} onSave={v=>updateLiability(l.id,"interest_rate",v)} prefix="" style={{ fontSize:13, fontWeight:600, color:l.interest_rate?C.amber:C.textSoft }}/>
                  <span style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>%</span>
                </div>
                <div>
                  <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT, marginBottom:2 }}>Monthly Interest</div>
                  <div style={{ fontSize:13, fontWeight:600, color:monthlyInt?C.rose:C.textSoft, fontFamily:MONO }}>{monthlyInt ? fmt(monthlyInt) : "Add rate →"}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop:12, padding:"10px 16px", background:C.amberLight, borderRadius:10, border:`1px solid ${C.amber}33` }}>
          <div style={{ fontSize:12, color:C.amber, fontFamily:FONT }}>💡 Click any balance or rate to edit — changes save instantly to your database.</div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Assets — click to edit</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {assets.map(a=>(
              <div key={a.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:C.bg, borderRadius:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:16 }}>{a.icon}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{a.name}</div>
                    <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{a.type}</div>
                  </div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <EditableNum value={a.value} onSave={v=>updateAsset(a.id,v)} style={{ fontSize:13, fontWeight:700, color:C.text }}/>
                  <div style={{ fontSize:11, color:Number(a.change_pct)>0?C.green:C.rose, fontFamily:FONT }}>{Number(a.change_pct)>0?"▲":"▼"} {Math.abs(a.change_pct)}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Liabilities — click to edit</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {liabilities.filter(l=>l.icon!=="🎓").map(l=>(
              <div key={l.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:C.bg, borderRadius:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:16 }}>{l.icon}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{l.name}</div>
                    <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{l.type}</div>
                  </div>
                </div>
                <EditableNum value={l.balance} onSave={v=>updateLiability(l.id,"balance",v)} style={{ fontSize:13, fontWeight:700, color:C.rose }}/>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── GOALS ─────────────────────────────────────────────────────────────────────
function GoalsSection() {
  const [goals, setGoals] = useState(null);

  useEffect(()=>{ dbGet("goals","?order=target_amount.desc").then(setGoals); },[]);

  const updateGoal = async (id,field,val) => {
    await dbPatch("goals",id,{[field]:val});
    setGoals(prev=>prev.map(g=>g.id===id?{...g,[field]:val}:g));
  };

  if (!goals) return <Spinner />;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:16 }}>
        {goals.map(g=>{
          const pct = Math.round((Number(g.current_amount)/Number(g.target_amount))*100);
          return (
            <div key={g.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                <div>
                  <div style={{ fontSize:22, marginBottom:4 }}>{g.icon}</div>
                  <div style={{ fontSize:16, fontWeight:700, color:C.text, fontFamily:FONT }}>{g.name}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:28, fontWeight:800, color:g.color, fontFamily:MONO }}>{pct}%</div>
                  <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>funded</div>
                </div>
              </div>
              <ProgressBar current={Number(g.current_amount)} target={Number(g.target_amount)} color={g.color}/>
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:12 }}>
                <div>
                  <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT }}>Saved</div>
                  <EditableNum value={g.current_amount} onSave={v=>updateGoal(g.id,"current_amount",v)} style={{ fontSize:14, fontWeight:700, color:g.color }}/>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT }}>Target</div>
                  <EditableNum value={g.target_amount} onSave={v=>updateGoal(g.id,"target_amount",v)} style={{ fontSize:14, fontWeight:700, color:C.text }}/>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── DOCUMENTS ─────────────────────────────────────────────────────────────────
function DocsSection() {
  const [docs, setDocs] = useState(null);
  useEffect(()=>{ dbGet("documents","?order=created_at.desc").then(setDocs); },[]);
  if (!docs) return <Spinner />;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Document Vault</div>
        {docs.length === 0 ? (
          <div style={{ textAlign:"center", padding:"30px 0", color:C.textSoft, fontFamily:FONT, fontSize:13 }}>No documents yet. Add passports, insurance policies, wills...</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {docs.map(d=>(
              <div key={d.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 16px", background:d.status==="expiring"?C.amberLight:C.bg, border:`1px solid ${d.status==="expiring"?C.amber+"44":C.border}`, borderRadius:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <span style={{ fontSize:18 }}>{d.icon}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{d.name}</div>
                    <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{d.type} · {d.expires_on || "No expiry"}</div>
                  </div>
                </div>
                <span style={{ fontSize:11, fontWeight:600, padding:"4px 10px", borderRadius:99, background:d.status==="expiring"?C.amber+"22":C.green+"22", color:d.status==="expiring"?C.amber:C.green, fontFamily:FONT }}>
                  {d.status==="expiring"?"EXPIRING":"CURRENT"}
                </span>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop:14, padding:"12px 16px", background:C.blueLight, borderRadius:12, border:`1px dashed ${C.blue}44`, cursor:"pointer", textAlign:"center" }}>
          <span style={{ fontSize:13, color:C.blue, fontWeight:600, fontFamily:FONT }}>+ Add Document — coming soon</span>
        </div>
      </div>
    </div>
  );
}

// ── LIFE ADMIN ────────────────────────────────────────────────────────────────
function CalendarSection() {
  const events = [
    { date:"Mar 1",  label:"Mortgage payment due · Wells Fargo",   type:"bill",   icon:"🏡" },
    { date:"Mar 1",  label:"Michael & Sarah salaries",              type:"income", icon:"💵" },
    { date:"Mar 1",  label:"HELOC payment due · Wells Fargo",       type:"bill",   icon:"💳" },
    { date:"Mar 5",  label:"Honda auto loan payment",               type:"bill",   icon:"🚗" },
    { date:"Mar 10", label:"Navient student loan payments · Sarah", type:"bill",   icon:"🎓" },
    { date:"Mar 15", label:"Michael & Sarah salaries",              type:"income", icon:"💵" },
    { date:"Mar 19", label:"Liam's Birthday 🎂",                    type:"alert",  icon:"🎉" },
    { date:"Apr 1",  label:"Q1 estimated taxes due",                type:"tax",    icon:"🏛️" },
    { date:"Apr 15", label:"Tax Day — file or extend",              type:"tax",    icon:"📋" },
    { date:"May 19", label:"Liam turns 18 🎂",                      type:"alert",  icon:"🎓" },
    { date:"Jul 14", label:"Sarah's Birthday 🎂",                   type:"alert",  icon:"🎉" },
    { date:"Sep 15", label:"Auto Insurance renewal · USAA",         type:"bill",   icon:"🚗" },
    { date:"Sep 30", label:"Zoe's Birthday 🎂",                     type:"alert",  icon:"🎉" },
    { date:"Nov 1",  label:"Home Insurance renewal · State Farm",   type:"bill",   icon:"🏡" },
    { date:"Nov 8",  label:"Emma's Birthday 🎂",                    type:"alert",  icon:"🎉" },
  ];
  const typeColor = { bill:C.rose, income:C.green, tax:C.amber, alert:C.violet };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Upcoming — March → November</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {events.map((e,i)=>(
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", background:C.bg, borderRadius:12, borderLeft:`3px solid ${typeColor[e.type]}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:16 }}>{e.icon}</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{e.label}</div>
                  <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{e.date}</div>
                </div>
              </div>
              <span style={{ fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:99, background:typeColor[e.type]+"18", color:typeColor[e.type], fontFamily:FONT, textTransform:"uppercase", letterSpacing:0.5 }}>
                {e.type}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── AI PLANNER PANEL ──────────────────────────────────────────────────────────
function AIPlannerPanel({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [context, setContext]   = useState(null);
  const endRef = useRef(null);

  useEffect(() => { if (isOpen && !context) loadContext(); }, [isOpen, context]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages, loading]);

  const loadContext = async () => {
    try {
      const [assets, liabilities, goals, members, profiles, accounts, cats] = await Promise.all([
        dbGet("assets","?order=value.desc"),
        dbGet("liabilities"),
        dbGet("goals"),
        dbGet("family_members","?order=created_at.asc"),
        dbGet("member_profiles"),
        dbGet("member_accounts","?order=sort_order.asc"),
        dbGet("budget_categories"),
      ]);
      const totalAssets = assets.reduce((s,a)=>s+Number(a.value),0);
      const totalLiab   = liabilities.reduce((s,l)=>s+Number(l.balance),0);
      const totalIncome = profiles.reduce((s,p)=>s+(p.salary||0),0);
      const debts       = accounts.filter(a=>a.is_debt);
      setContext(
`Harper Family Financial Data — March 2026
NET WORTH: $${(totalAssets-totalLiab).toLocaleString()} (Assets $${totalAssets.toLocaleString()}, Liabilities $${totalLiab.toLocaleString()})
INCOME: $${totalIncome.toLocaleString()}/yr combined ($${Math.round(totalIncome/12).toLocaleString()}/mo)
FAMILY: ${members.map(m=>`${m.name} (${m.role})`).join(", ")}
GOALS: ${goals.map(g=>`${g.name}: $${Number(g.current_amount).toLocaleString()}/$${Number(g.target_amount).toLocaleString()} (${Math.round((g.current_amount/g.target_amount)*100)}%)`).join("; ")}
DEBTS: ${debts.map(d=>`${d.label} $${(d.balance||0).toLocaleString()} @ ${d.interest_rate||'?'}% APR`).join("; ")}
ASSETS: ${assets.slice(0,6).map(a=>`${a.name} $${Number(a.value).toLocaleString()}`).join("; ")}
BUDGET CATEGORIES: ${cats.map(c=>c.name).join(", ")}`
      );
    } catch(e) { setContext("Financial data unavailable."); }
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role:"user", content:input.trim() };
    const next = [...messages, userMsg];
    setMessages(next); setInput(""); setLoading(true);
    try {
      const system = `You are Cuan, a warm and knowledgeable family financial advisor. You have the Harper family's complete financial picture below. Answer questions specifically using their real data. Be concise and actionable — avoid generic advice.\n\n${context||"Loading data…"}`;
      const reply = await callClaude(next, system);
      setMessages(prev => [...prev, { role:"assistant", content:reply }]);
    } catch(e) {
      const msg = e.message.includes("CLAUDE_API_KEY")||e.message.includes("503")||e.message.includes("404")
        ? "⚙️ Claude API not configured yet. Add CLAUDE_API_KEY to Vercel environment variables to activate."
        : `Sorry, something went wrong: ${e.message}`;
      setMessages(prev => [...prev, { role:"assistant", content:msg }]);
    } finally { setLoading(false); }
  };

  const STARTERS = [
    "What's our best debt payoff strategy?",
    "Are we on track for retirement?",
    "How much should we save for college?",
    "What's our debt-to-income ratio?",
  ];

  if (!isOpen) return null;
  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.25)", zIndex:200 }} />
      <div style={{ position:"fixed", top:0, right:0, bottom:0, width:420, background:C.surface, zIndex:201, display:"flex", flexDirection:"column", boxShadow:"-8px 0 40px rgba(0,0,0,0.14)" }}>
        {/* Header */}
        <div style={{ padding:"20px 24px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", background:"linear-gradient(135deg,#1A2740 0%,#2B3F63 100%)", flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:"#fff", fontFamily:FONT }}>✨ AI Financial Planner</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", fontFamily:FONT, marginTop:2 }}>Powered by Claude · Harper family data loaded</div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:8, width:32, height:32, cursor:"pointer", color:"#fff", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>
        {/* Messages */}
        <div style={{ flex:1, overflowY:"auto", padding:"20px 20px 8px" }}>
          {messages.length === 0 && (
            <div style={{ textAlign:"center", paddingTop:24 }}>
              <div style={{ fontSize:28, marginBottom:10 }}>⚓</div>
              <div style={{ fontSize:14, fontWeight:600, color:C.text, fontFamily:FONT, marginBottom:6 }}>Your Family's Financial Advisor</div>
              <div style={{ fontSize:12, color:C.textSoft, fontFamily:FONT, marginBottom:22 }}>Ask anything about your finances</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {STARTERS.map(q=>(
                  <button key={q} onClick={()=>setInput(q)}
                    style={{ padding:"10px 14px", background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, fontSize:12, color:C.textMid, fontFamily:FONT, cursor:"pointer", textAlign:"left" }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m,i)=>(
            <div key={i} style={{ marginBottom:14, display:"flex", flexDirection:"column", alignItems:m.role==="user"?"flex-end":"flex-start" }}>
              <div style={{ maxWidth:"85%", padding:"11px 15px", borderRadius:m.role==="user"?"16px 16px 4px 16px":"16px 16px 16px 4px", background:m.role==="user"?C.blue:C.bg, color:m.role==="user"?"#fff":C.text, fontSize:13, fontFamily:FONT, lineHeight:1.6, whiteSpace:"pre-wrap" }}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display:"flex", alignItems:"flex-start", marginBottom:14 }}>
              <div style={{ padding:"11px 15px", borderRadius:"16px 16px 16px 4px", background:C.bg, fontSize:13, color:C.textSoft, fontFamily:FONT }}>Thinking…</div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        {/* Input */}
        <div style={{ padding:"14px 18px", borderTop:`1px solid ${C.border}`, flexShrink:0 }}>
          <div style={{ display:"flex", gap:8 }}>
            <input value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); send(); } }}
              placeholder="Ask about your finances…"
              style={{ flex:1, padding:"11px 14px", border:`1px solid ${C.border}`, borderRadius:12, fontSize:13, fontFamily:FONT, outline:"none", color:C.text, background:C.bg }} />
            <button onClick={send} disabled={loading||!input.trim()}
              style={{ background:input.trim()&&!loading?C.blue:C.border, color:input.trim()&&!loading?"#fff":C.textSoft, border:"none", borderRadius:12, padding:"11px 18px", fontSize:13, fontWeight:600, fontFamily:FONT, cursor:input.trim()&&!loading?"pointer":"default" }}>
              Send
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── DEBT PAYOFF MODELER ────────────────────────────────────────────────────────
function DebtSection() {
  const [accounts,    setAccounts]    = useState(null);
  const [members,     setMembers]     = useState(null);
  const [liabilities, setLiabilities] = useState(null);
  const [extra,       setExtra]       = useState(500);
  const [editExtra,   setEditExtra]   = useState(false);
  const [extraInput,  setExtraInput]  = useState("500");

  useEffect(() => {
    Promise.all([
      dbGet("member_accounts","?order=sort_order.asc"),
      dbGet("family_members","?order=created_at.asc"),
      dbGet("liabilities","?order=balance.desc"),
    ]).then(([a,m,l]) => { setAccounts(a||[]); setMembers(m||[]); setLiabilities(l||[]); });
  }, []);

  if (!accounts) return <Spinner />;

  // Build unified debt list from member_accounts (is_debt) + liabilities with rates
  const memberDebts = accounts
    .filter(a => a.is_debt && a.balance > 0)
    .map(a => ({
      id: a.id,
      name: a.label,
      owner: members.find(m=>m.id===a.member_id)?.name || "Family",
      balance: Number(a.balance),
      rate: Number(a.interest_rate||0),
      minPayment: Math.max(Math.round(Number(a.balance)*0.01), 50),
    }));

  // Add household liabilities that have interest rates (skip student loans already in member_accounts)
  const liabDebts = (liabilities||[])
    .filter(l => Number(l.interest_rate)>0 && l.icon!=="🎓" && Number(l.balance)>0)
    .map(l => ({
      id: "l_"+l.id,
      name: l.name,
      owner: "Family",
      balance: Number(l.balance),
      rate: Number(l.interest_rate),
      minPayment: Math.max(Math.round(Number(l.balance)*0.005), 100),
    }));

  const allDebts = [...memberDebts, ...liabDebts];
  if (allDebts.length === 0) return (
    <div style={{ textAlign:"center", padding:"60px 0", color:C.textSoft, fontFamily:FONT }}>No debts with interest rates found.</div>
  );

  const totalDebt   = allDebts.reduce((s,d)=>s+d.balance, 0);
  const totalMinPay = allDebts.reduce((s,d)=>s+d.minPayment, 0);

  // Simulation engine
  function simulate(debts, extraMonthly, method) {
    let d = debts.map(x=>({...x}));
    let totalInterest = 0, months = 0;
    const history = [];
    while (d.some(x=>x.balance>0.01) && months<720) {
      months++;
      // Accrue interest
      for (const x of d) {
        if (x.balance<=0) continue;
        const int = x.balance*(x.rate/100/12);
        totalInterest += int;
        x.balance += int;
      }
      // Pay minimums
      for (const x of d) {
        if (x.balance<=0) continue;
        x.balance = Math.max(0, x.balance - Math.min(x.minPayment, x.balance));
      }
      // Apply extra to priority debt
      const active = d.filter(x=>x.balance>0);
      const sorted = method==="avalanche"
        ? [...active].sort((a,b)=>b.rate-a.rate)
        : [...active].sort((a,b)=>a.balance-b.balance);
      let rem = extraMonthly;
      for (const p of sorted) {
        if (rem<=0) break;
        const pay = Math.min(rem, p.balance);
        p.balance = Math.max(0, p.balance-pay);
        rem -= pay;
      }
      history.push({ month:months, balance:Math.round(d.reduce((s,x)=>s+x.balance,0)) });
    }
    return { months, totalInterest:Math.round(totalInterest), history };
  }

  const av = simulate(allDebts, extra, "avalanche");
  const sw = simulate(allDebts, extra, "snowball");
  const interestSaved = sw.totalInterest - av.totalInterest;
  const monthsSaved   = sw.months - av.months;
  const winner = av.totalInterest <= sw.totalInterest ? "avalanche" : "snowball";

  // Chart — sample every 3 months
  const maxM = Math.max(av.months, sw.months);
  const chartData = [];
  for (let i=0; i<=maxM; i+=Math.max(1,Math.floor(maxM/20))) {
    chartData.push({
      m: i===0 ? "Now" : `${i}mo`,
      Avalanche: av.history[i]?.balance ?? 0,
      Snowball:  sw.history[i]?.balance ?? 0,
    });
  }
  if (!chartData.find(d=>d.Avalanche===0)) chartData.push({ m:"✓", Avalanche:0, Snowball:0 });

  const byRate = [...allDebts].sort((a,b)=>b.rate-a.rate);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {/* Top stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
        <StatCard label="Total Debt" value={fmt(totalDebt)} sub={`${allDebts.length} loans`} icon="📋" />
        <StatCard label="Est. Minimums" value={fmt(totalMinPay)} sub="per month" icon="💳" />
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 24px", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", fontFamily:FONT, marginBottom:8 }}>Extra Monthly Payment</div>
          {editExtra ? (
            <input autoFocus type="number" value={extraInput}
              onChange={e=>setExtraInput(e.target.value)}
              onBlur={()=>{ setEditExtra(false); const n=parseInt(extraInput); if(!isNaN(n)&&n>=0) setExtra(n); }}
              onKeyDown={e=>{ if(e.key==="Enter"){ setEditExtra(false); const n=parseInt(extraInput); if(!isNaN(n)&&n>=0) setExtra(n); }}}
              style={{ fontSize:28, fontWeight:700, color:C.blue, fontFamily:MONO, border:`1px solid ${C.blue}`, borderRadius:8, padding:"2px 8px", width:140, outline:"none", background:C.blueLight }} />
          ) : (
            <div onClick={()=>{ setEditExtra(true); setExtraInput(String(extra)); }} style={{ cursor:"pointer" }}>
              <div style={{ fontSize:28, fontWeight:700, color:C.blue, fontFamily:MONO, letterSpacing:-1 }}>{fmt(extra)}</div>
              <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT, marginTop:4 }}>click to change</div>
            </div>
          )}
        </div>
      </div>

      {/* Method comparison */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        {[
          { key:"avalanche", label:"🏔️ Avalanche", sub:"Highest interest rate first", color:C.blue, data:av },
          { key:"snowball",  label:"⛄ Snowball",   sub:"Lowest balance first",        color:C.violet, data:sw },
        ].map(({ key, label, sub, color, data })=>(
          <div key={key} style={{ background:C.card, border:`2px solid ${winner===key?color:C.border}`, borderRadius:16, padding:24, position:"relative", boxShadow:winner===key?`0 4px 20px ${color}22`:"0 1px 4px rgba(0,0,0,0.06)" }}>
            {winner===key && (
              <div style={{ position:"absolute", top:-11, right:16, background:color, color:"#fff", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:99, fontFamily:FONT }}>RECOMMENDED</div>
            )}
            <div style={{ fontSize:15, fontWeight:700, color:C.text, fontFamily:FONT, marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:12, color:C.textSoft, fontFamily:FONT, marginBottom:20 }}>{sub}</div>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <div style={{ fontSize:10, color:C.textSoft, textTransform:"uppercase", letterSpacing:0.5, fontFamily:FONT }}>Debt-Free In</div>
                <div style={{ fontSize:28, fontWeight:800, color, fontFamily:MONO, marginTop:2 }}>{data.months} <span style={{ fontSize:14, fontWeight:500 }}>mo</span></div>
                <div style={{ fontSize:12, color:C.textSoft, fontFamily:FONT }}>{Math.floor(data.months/12)}y {data.months%12}mo · ~{new Date(Date.now()+data.months*30.44*24*3600*1000).toLocaleDateString("en-US",{month:"short",year:"numeric"})}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:C.textSoft, textTransform:"uppercase", letterSpacing:0.5, fontFamily:FONT }}>Total Interest</div>
                <div style={{ fontSize:22, fontWeight:700, color:C.rose, fontFamily:MONO, marginTop:2 }}>{fmt(data.totalInterest)}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:C.textSoft, textTransform:"uppercase", letterSpacing:0.5, fontFamily:FONT }}>Total Paid</div>
                <div style={{ fontSize:16, fontWeight:600, color:C.text, fontFamily:MONO, marginTop:2 }}>{fmt(totalDebt+data.totalInterest)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Savings callout */}
      {interestSaved > 50 && (
        <div style={{ background:C.blueLight, border:`1px solid ${C.blue}33`, borderRadius:14, padding:"16px 20px", display:"flex", alignItems:"center", gap:14 }}>
          <span style={{ fontSize:22 }}>💡</span>
          <div style={{ fontFamily:FONT }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.blue }}>Avalanche saves {fmt(interestSaved)} in interest</div>
            <div style={{ fontSize:12, color:C.textSoft, marginTop:2 }}>
              {monthsSaved>0?`That's ${monthsSaved} months faster and `:""}{fmt(interestSaved)} more staying in your pocket vs. Snowball.
            </div>
          </div>
        </div>
      )}

      {/* Payoff chart */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Payoff Timeline</div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="avG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.blue} stopOpacity={0.15}/><stop offset="95%" stopColor={C.blue} stopOpacity={0}/></linearGradient>
              <linearGradient id="swG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.violet} stopOpacity={0.15}/><stop offset="95%" stopColor={C.violet} stopOpacity={0}/></linearGradient>
            </defs>
            <XAxis dataKey="m" tick={{fontSize:10,fill:C.textSoft,fontFamily:FONT}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fontSize:10,fill:C.textSoft,fontFamily:MONO}} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
            <Tooltip formatter={v=>[fmt(v),""]} contentStyle={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,fontSize:12,fontFamily:FONT}}/>
            <Area type="monotone" dataKey="Avalanche" stroke={C.blue}   strokeWidth={2} fill="url(#avG)" dot={false}/>
            <Area type="monotone" dataKey="Snowball"  stroke={C.violet} strokeWidth={2} fill="url(#swG)" dot={false}/>
          </AreaChart>
        </ResponsiveContainer>
        <div style={{ display:"flex", gap:20, justifyContent:"center", marginTop:4 }}>
          {[["Avalanche",C.blue],["Snowball",C.violet]].map(([l,c])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:12, height:3, background:c, borderRadius:2 }}/>
              <span style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Debt breakdown */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.textSoft, letterSpacing:0.5, textTransform:"uppercase", marginBottom:16, fontFamily:FONT }}>Avalanche Order — Tackle These First</div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {byRate.map((d,i)=>(
            <div key={d.id} style={{ display:"grid", gridTemplateColumns:"1fr 110px 90px 70px", gap:12, alignItems:"center", padding:"12px 16px", background:i===0?C.blueLight:C.bg, borderRadius:10, border:i===0?`1px solid ${C.blue}33`:"none" }}>
              <div>
                <div style={{ fontSize:13, fontWeight:500, color:C.text, fontFamily:FONT }}>{d.name}</div>
                <div style={{ fontSize:11, color:C.textSoft, fontFamily:FONT }}>{d.owner}</div>
              </div>
              <div style={{ fontSize:13, fontWeight:700, color:C.rose, fontFamily:MONO }}>{fmt(d.balance)}</div>
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:d.rate>10?C.rose:d.rate>6?C.amber:C.textSoft, fontFamily:FONT }}>{d.rate}% APR</div>
                <div style={{ fontSize:10, color:C.textSoft, fontFamily:FONT }}>{fmt(Math.round(d.balance*d.rate/100/12))}/mo interest</div>
              </div>
              <div style={{ fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:99, background:i===0?C.blue:"transparent", color:i===0?"#fff":C.textSoft, fontFamily:FONT, textAlign:"center" }}>
                {i===0?"🎯 #1":`#${i+1}`}
              </div>
            </div>
          ))}
        </div>
        {byRate[0] && (
          <div style={{ marginTop:12, padding:"10px 14px", background:C.amberLight, borderRadius:10, fontSize:12, color:C.amber, fontFamily:FONT }}>
            💡 Attack <strong>{byRate[0].name}</strong> first — at {byRate[0].rate}% APR it's costing {fmt(Math.round(byRate[0].balance*byRate[0].rate/100/12))}/month in interest alone.
          </div>
        )}
      </div>
    </div>
  );
}

// ── NAV + SHELL ───────────────────────────────────────────────────────────────
const NAV = [
  { id:"overview",  label:"Overview",          icon:"◈" },
  { id:"d1", divider:true, label:"FINANCES" },
  { id:"money",     label:"Monthly Spend",     icon:"💰" },
  { id:"goals",     label:"Goals",             icon:"🎯" },
  { id:"debt",      label:"Debt Payoff",       icon:"📊" },
  { id:"d2", divider:true, label:"FAMILY" },
  { id:"family",    label:"Members",           icon:"👨‍👩‍👧" },
  { id:"assets",    label:"Assets & Property", icon:"🏠" },
  { id:"docs",      label:"Documents",         icon:"📋" },
  { id:"d3", divider:true, label:"PLANNING" },
  { id:"calendar",  label:"Life Admin",        icon:"📅" },
];

export default function CuanApp() {
  const [active, setActive]         = useState("overview");
  const [aiPanelOpen, setAiPanel]   = useState(false);
  const { user } = useAuth();
  const activeNav = NAV.find(n=>n.id===active);

  const sections = {
    overview: <OverviewSection/>,
    money: <MoneySection/>,
    family: <FamilySection/>,
    assets: <AssetsSection/>,
    docs: <DocsSection/>,
    goals: <GoalsSection/>,
    calendar: <CalendarSection/>,
    debt: <DebtSection/>,
  };

  return (
    <>
    <div style={{ display:"flex", height:"100vh", background:C.bg, fontFamily:FONT, overflow:"hidden" }}>
      {/* Sidebar */}
      <div style={{ width:220, background:C.sidebar, display:"flex", flexDirection:"column", flexShrink:0 }}>
        <div style={{ padding:"24px 20px 20px", borderBottom:"1px solid #ffffff14" }}>
          <div style={{ fontSize:22, fontWeight:800, color:"#FFFFFF", fontFamily:MONO, letterSpacing:-1 }}>cuan</div>
          <div style={{ fontSize:9, color:"#374151", letterSpacing:1.5, textTransform:"uppercase", fontFamily:FONT, marginTop:2 }}>your family's safe harbor</div>
        </div>
        <div style={{ flex:1, padding:"12px 8px", overflowY:"auto" }}>
          {NAV.map(item=>{
            if(item.divider) return <div key={item.id} style={{ padding:"16px 12px 6px", fontSize:9, fontWeight:700, color:"#374151", letterSpacing:1.5, textTransform:"uppercase", fontFamily:FONT }}>{item.label}</div>;
            const isActive = active===item.id;
            return (
              <div key={item.id} onClick={()=>setActive(item.id)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:8, cursor:"pointer", marginBottom:2, background:isActive?C.sidebarActive:"transparent" }}>
                <span style={{ fontSize:14 }}>{item.icon}</span>
                <span style={{ fontSize:13, fontWeight:isActive?600:400, color:isActive?"#FFFFFF":C.textSidebar, fontFamily:FONT }}>{item.label}</span>
                {isActive && <div style={{ marginLeft:"auto", width:5, height:5, borderRadius:99, background:C.blue }}/>}
              </div>
            );
          })}
        </div>
        {/* Sidebar footer — live from Supabase Auth */}
        <div style={{ padding:"16px", borderTop:"1px solid #ffffff14" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:30, height:30, borderRadius:15, background:C.blue+"33",
              border:`2px solid ${C.blue}66`, display:"flex", alignItems:"center",
              justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:12, color:C.blue }}>⚓</span>
            </div>
            <div style={{ overflow:"hidden" }}>
              <div style={{ fontSize:12, fontWeight:600, color:"#CBD5E1", fontFamily:FONT,
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {user?.email ?? "Harper Family"}
              </div>
              <div style={{ fontSize:10, color:"#374151", fontFamily:FONT }}>cuan.money · live db</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:"16px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0, boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
          <div>
            <div style={{ fontSize:19, fontWeight:700, color:C.text, fontFamily:FONT }}>{activeNav?.icon} {activeNav?.label}</div>
            <div style={{ fontSize:12, color:C.textSoft, fontFamily:FONT, marginTop:1 }}>March 2026 · Live data from Supabase</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ fontSize:12, color:C.green, fontWeight:500, fontFamily:FONT, padding:"6px 14px", background:C.greenLight, borderRadius:20, border:`1px solid ${C.green}33` }}>
              ✓ Database connected
            </div>
            <button onClick={()=>setAiPanel(true)}
              style={{ display:"flex", alignItems:"center", gap:7, padding:"7px 16px", background:"linear-gradient(135deg,#7C3AED,#2563EB)", border:"none", borderRadius:20, fontSize:12, fontWeight:600, color:"#fff", fontFamily:FONT, cursor:"pointer", boxShadow:"0 2px 8px rgba(124,58,237,0.3)" }}>
              ✨ AI Planner
            </button>
            <SignOutButton style={{ border:`1.5px solid ${C.border}`, color:C.textSoft }} />
          </div>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"24px 28px" }}>
          {sections[active]}
        </div>
      </div>
    </div>

    <AIPlannerPanel isOpen={aiPanelOpen} onClose={()=>setAiPanel(false)} />
    </>
  );
}
